import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-sand">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-forest/70 hover:text-forest mb-8">
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>
        
        <h1 className="font-display text-4xl font-bold text-forest mb-8">Privacy Policy</h1>
        
        <div className="prose prose-forest max-w-none space-y-6 text-forest/80">
          <p className="text-sm text-muted-foreground">Last updated: March 21, 2026</p>
          
          <section>
            <h2 className="font-display text-xl font-semibold text-forest mt-8 mb-4">1. Introduction</h2>
            <p>
              ProspectConnect ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, and safeguard your information when you use our proposal management platform.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-forest mt-8 mb-4">2. Information We Collect</h2>
            <p>We collect only the information necessary to provide our service:</p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li><strong>Account information:</strong> Name, email address, and company name</li>
              <li><strong>Proposal content:</strong> The proposals you create and client information you enter</li>
              <li><strong>Communications:</strong> Messages you send us for support</li>
            </ul>
            <p className="mt-4 p-3 bg-forest/5 rounded-lg">
              <strong>What we do NOT store:</strong> We never store credit card numbers, bank account details, or any payment credentials. All payment processing is handled directly by our payment provider—your financial information never touches our servers.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-forest mt-8 mb-4">3. How We Use Your Information</h2>
            <p>We use your information solely to:</p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li>Provide and maintain our proposal management service</li>
              <li>Send proposals to your clients on your behalf</li>
              <li>Send you important service notifications</li>
              <li>Respond to your support requests</li>
            </ul>
            <p className="mt-3">We do not sell, rent, or share your information for marketing purposes.</p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-forest mt-8 mb-4">4. Third-Party Service Providers</h2>
            <p>
              We work with trusted service providers who perform essential functions on our behalf, including payment processing, email delivery, and electronic signatures. These providers only receive the minimum information necessary to perform their specific service and are contractually bound to protect your data.
            </p>
            <p className="mt-3">
              We do not sell or share your data with advertisers or data brokers.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-forest mt-8 mb-4">5. Data Security</h2>
            <p>We take the security of your data seriously and implement multiple layers of protection:</p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li><strong>Encryption at rest:</strong> All sensitive data and credentials are encrypted using industry-standard encryption</li>
              <li><strong>Encryption in transit:</strong> All data transmitted between your browser and our servers is protected with TLS/SSL encryption</li>
              <li><strong>Password security:</strong> Passwords are hashed using bcrypt with salt—we cannot see or recover your password</li>
              <li><strong>Secure infrastructure:</strong> Our servers are hosted in secure, monitored data centers</li>
              <li><strong>Access controls:</strong> Strict internal access controls limit who can access system data</li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-forest mt-8 mb-4">6. Data Retention & Deletion</h2>
            <p>
              We retain your information only as long as your account is active. You have full control over your data:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li><strong>Delete your account anytime:</strong> Go to Settings and click "Delete Account" — all your data is permanently removed immediately</li>
              <li><strong>Delete individual proposals:</strong> Remove any proposal at any time from your dashboard</li>
              <li><strong>No waiting period:</strong> Deletion is instant and irreversible</li>
            </ul>
            <p className="mt-3">
              Once deleted, your data cannot be recovered by us or anyone else.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-forest mt-8 mb-4">7. Your Rights</h2>
            <p>You have the right to:</p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li>Access your personal data at any time through your account</li>
              <li>Correct any inaccurate information in your profile settings</li>
              <li>Delete your account and all associated data instantly</li>
              <li>Export your proposals and data</li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-forest mt-8 mb-4">8. Contact Us</h2>
            <p>
              If you have questions about this Privacy Policy or how we handle your data, please contact us at:
            </p>
            <p className="mt-3">
              <strong>Email:</strong> <a href="mailto:support@prospectconnect.com" className="text-accent hover:underline">support@prospectconnect.com</a>
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
